/*
 * Every colour, space, radius, font and duration here is a design token.
 * The only literals are structural hairlines, the 2px indicator, and --spring.
 */

.base,
.base *,
.base *::before,
.base *::after {
  box-sizing: border-box;
}

.base {
  display: flex;
  flex-direction: column;

  /* The one curve in the file that isn't a token, because nothing else uses it yet.
     The reference has four different overshoot curves and no view on which is the
     spring; promote this to @parrot/tokens when a second component wants it, at
     which point we'll know whether they're the same decision or two that agree. */
  --spring: cubic-bezier(0.37, 1.95, 0.66, 0.56);
}

/* ── Tab list ── */

.list {
  position: relative;
  display: flex;

  padding-top: var(--space-4);
  margin-bottom: var(--space-5);
  border-bottom: 1px solid var(--border-default);

  /* A tab set that outgrows its container scrolls rather than wraps. The scrollbar
     is hidden because it would be a third of the height of the row it sits under. */
  overflow-x: auto;
  scrollbar-width: none;
}

.list::-webkit-scrollbar {
  display: none;
}

/* The sliding indicator. TabList measures the active tab and writes the two custom
   properties; everything else, including the glow, derives from --active so it
   follows the theme instead of pinning one theme's gold. */
.list::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: var(--tab-indicator-left, 0);
  width: var(--tab-indicator-width, 0);
  height: 2px;

  background: var(--active);
  /* Tight glow on the bar, wide wash above it. */
  box-shadow:
    0 0 8px 1px color-mix(in srgb, var(--active) 60%, transparent),
    0 -12px 28px color-mix(in srgb, var(--active) 25%, transparent);

  /* Width settles before position does, which is what makes it stretch into place
     rather than glide. --spring overshoots the target and eases back. */
  transition:
    left var(--duration-slow) var(--spring),
    width var(--duration-base) var(--spring);
}

/* ── Tab ── */

.tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);

  padding: var(--space-3) var(--space-4);
  border: 0;
  background: none;

  font-family: var(--font-sans);
  font-size: var(--text-base);
  font-weight: var(--weight-bold);
  line-height: var(--leading-tight);
  white-space: nowrap;
  color: var(--text-muted);
  cursor: pointer;

  transition: color var(--duration-base) var(--ease-default);
}

/* An icon is just a child, as in Badge — no slot prop. Sized in em against the label
   so it tracks the font, and so consumers don't have to pass a pixel size to every
   one of them. Unsized SVGs otherwise render at 300x150. */
.tab > svg {
  width: 1.15em;
  height: 1.15em;
  flex-shrink: 0;
}

/* Hairline between adjacent tabs, centred rather than full-height. */
.tab + .tab::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: var(--space-5);
  background: var(--border-default);
}

.tab:hover:not(:disabled) {
  color: var(--text-primary);
}

/* The shared ring, but inset rather than the usual --focus-ring-offset: the list
   scrolls, and a scroll container clips on both axes, so an outward ring would lose
   its bottom edge and the first tab would lose its left. */
.tab:focus-visible {
  outline: var(--focus-ring);
  outline-offset: -2px;
  border-radius: var(--radius-sm);
}

/* Dimmed by colour, not opacity — opacity would take the separator hairline with it,
   and a wizard's tab strip is mostly disabled tabs. */
.tab:disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}

/* Hover must not win over active. Icons need no rule at all: an SVG on
   stroke="currentColor" already inherits this. */
.tab.tabActive,
.tab.tabActive:hover {
  color: var(--active);
  background: linear-gradient(
    0deg,
    color-mix(in srgb, var(--active) 9%, transparent) 0%,
    transparent 80%
  );
}

/* ── Count ── */

.count {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* No margin: the tab's gap already spaces it off the label. */
  min-width: var(--space-5);
  padding-inline: var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--bg-elevated);

  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

.tabActive .count {
  border-color: color-mix(in srgb, var(--active) 40%, transparent);
  background: color-mix(in srgb, var(--active) 12%, transparent);
  color: var(--active);
}

/* ── Panel ── */

.panel {
  padding-top: var(--space-4);
}

/* forceMount leaves inactive panels in the DOM. Beats a consumer className that
   sets display, which would otherwise defeat the hidden attribute. */
.panel[hidden] {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .list::after,
  .tab {
    transition: none;
  }
}
